import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';

import type {
  SubscriptionDTO,
  SubscriptionState as SubStateTSClient,
} from '@klangio/ai2notes-ts-client';
import { PaddleBillingService } from '@klangio/ai2notes-ts-client';
import { useEffect } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { SubscriptionState } from '../../../entities/subscription';

import { convertDate, convertUTCDateToLocalDate } from '../../../utils';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: '15px',
  },
  fixedWidth: {
    width: 160,
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  body: {
    padding: 0,
  },
  head: {
    background: theme.palette.primary.main,
  },
}));

const getSubscriptionStateDescription = (state: SubscriptionState | SubStateTSClient) => {
  switch (state) {
    case SubscriptionState.ACTIVE:
      return 'active';
    case SubscriptionState.PAUSED:
      return 'paused';
    case SubscriptionState.DEACTIVATED:
      return 'deactivationDate';
    case SubscriptionState.CANCELLATION_REQUESTED:
      return 'cancelled';
    case SubscriptionState.CANCELLED:
      return 'cancelled';
    case SubscriptionState.REFUNDED:
      return 'refunded';
    case SubscriptionState.TRIALING:
      return 'trail';
    case SubscriptionState.CREATED:
      return 'created';
    default:
      return 'unknown';
  }
};

export interface SubscriptionBoxProps {
  subscription: SubscriptionDTO;
  onCancelSubscription: (subscriptionId: string) => void;
}

const SubscriptionBox: React.FC<SubscriptionBoxProps> = ({
  subscription,
  onCancelSubscription,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const date = convertUTCDateToLocalDate(convertDate(subscription.creationDate));
  // @ts-ignore - key for SubscriptionState from ts-client are not that descriptive
  const isActiveSubscription = subscription.state === SubscriptionState.ACTIVE;
  const [paymentUrl, setPaymentUrl] = React.useState<string | null>(null);

  const handleCancelSubscription = () => {
    switch (subscription.store) {
      case 'PLAY_STORE': {
        window.open('https://myaccount.google.com/subscriptions', '_blank');
        break;
      }
      case 'APP_STORE': {
        window.open('https://apps.apple.com/account/subscriptions', '_blank');
        break;
      }
      default:
        onCancelSubscription(subscription.id);
    }
  };

  useEffect(() => {
    if (!paymentUrl && subscription?.providerId?.startsWith('sub_')) {
      PaddleBillingService.paddleBillingGetManagementUrls(subscription.providerId).then((data) => {
        setPaymentUrl(data.updatePaymentUrl);
      });
    } else {
      setPaymentUrl(subscription.updateUrl);
    }
  }, []);
  return (
    <Accordion>
      <AccordionSummary className={classes.head} expandIcon={<ExpandMoreIcon />}>
        {subscription.title}
      </AccordionSummary>
      <AccordionDetails className={classes.body}>
        <Table className={classes.container} aria-label="subscription info">
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                {t('dialog.user_info.subscription.state')}
              </TableCell>
              <TableCell className={classes.fixedWidth} align="right">
                {getSubscriptionStateDescription(subscription.state)}
              </TableCell>
            </TableRow>
            {
              // @ts-ignore - key for SubscriptionState from ts-client are not that descriptive
              subscription.state === SubscriptionState.CANCELLATION_REQUESTED && (
                <TableRow>
                  <TableCell>{t('dialog.user_info.subscription.valid_until')}</TableCell>
                  <TableCell align="right">
                    {convertUTCDateToLocalDate(
                      convertDate(subscription.deactivationDate),
                    ).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              )
            }
            <TableRow>
              <TableCell component="th" scope="row">
                {t('dialog.user_info.subscription.purchase_date')}
              </TableCell>
              <TableCell className={classes.fixedWidth} align="right">
                {date.toLocaleDateString()}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                {t('dialog.user_info.subscription.tickets')}
              </TableCell>
              <TableCell className={classes.fixedWidth} align="right">
                {subscription.tickets}
              </TableCell>
            </TableRow>
            {subscription.discount && (
              <TableRow>
                <TableCell component="th" scope="row">
                  <strong>{t('dialog.user_info.subscription.discount')}</strong>
                </TableCell>
                <TableCell className={classes.fixedWidth} align="right">
                  <strong>
                    {subscription.discount}
                    %
                  </strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className={classes.buttons}>
          {isActiveSubscription ? (
            <Button
              color="primary"
              rel="noopener"
              target="_blank"
              href=""
              onClick={() => handleCancelSubscription()}
            >
              {t('dialog.user_info.subscription.cancel_subscription')}
            </Button>
          ) : (
            <div />
          )}
          {paymentUrl && (
            <Button color="primary" href={paymentUrl}>
              {t('dialog.user_info.change_payment')}
            </Button>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default SubscriptionBox;
